/** Image selection for catalog and submission tracks. */

const TRACK_IMAGES_FOLDER = 'images/tracks';
const ARTIST_IMAGES_FOLDER = 'images/artists';

const getImageFilename = (assetKey) => {
  if (!assetKey) return null;
  if (assetKey.startsWith('http') || assetKey.startsWith('/')) {
    return assetKey;
  }
  return assetKey.includes('/') ? assetKey.split('/').pop() : assetKey;
};

const usesStagingImage = (status) => status === 'pending' || status === 'rejected';

const resolveProductionImageKey = (imageValue, folder) => {
  if (!imageValue) return null;

  if (imageValue.startsWith('http') || imageValue.startsWith('/')) {
    return imageValue;
  }

  if (imageValue.startsWith('images/')) {
    return imageValue;
  }

  const filename = getImageFilename(imageValue);
  if (!filename) return null;

  return `${folder}/${filename}`;
};

/** artists.image from the row matching track.artist_id (joined as artist_image in API responses). */
export const getArtistImage = (track) => {
  if (!track) return null;
  return track.artist_image ?? null;
};

export const getTrackImageKey = (track) => {
  if (!track) return null;
  return track.image || getArtistImage(track) || null;
};

export const getTrackImageType = (track) => {
  if (!track) return 'track_image';
  if (track.image) return 'track_image';
  if (getArtistImage(track)) return 'artist';
  return 'track_image';
};

/**
 * Production catalog tracks:
 * - mastertracklist.image → images/tracks/
 * - else artists.image (via artist_id) → images/artists/
 */
export const resolveCatalogTrackImage = (track) => {
  if (!track) {
    return { assetKey: null, assetType: 'track_image' };
  }

  if (track.image) {
    return {
      assetKey: resolveProductionImageKey(track.image, TRACK_IMAGES_FOLDER),
      assetType: 'track_image',
    };
  }

  const artistImage = getArtistImage(track);
  if (artistImage) {
    return {
      assetKey: resolveProductionImageKey(artistImage, ARTIST_IMAGES_FOLDER),
      assetType: 'artist',
    };
  }

  return { assetKey: null, assetType: 'track_image' };
};

/** Track cover only (does not fall back to artist). */
export const resolveCatalogTrackCoverImage = (track) => {
  if (!track?.image) {
    return { assetKey: null, assetType: 'track_image' };
  }

  return {
    assetKey: resolveProductionImageKey(track.image, TRACK_IMAGES_FOLDER),
    assetType: 'track_image',
  };
};

/** Artist image only. */
export const resolveCatalogArtistImage = (track) => {
  const artistImage = getArtistImage(track);
  if (!artistImage) {
    return { assetKey: null, assetType: 'artist' };
  }

  return {
    assetKey: resolveProductionImageKey(artistImage, ARTIST_IMAGES_FOLDER),
    assetType: 'artist',
  };
};

/** Pending/rejected submissions use staging; approved tracks use production paths. */
export const resolveAssetKey = (assetKey, assetType = 'track_image', status) => {
  if (!assetKey) return null;

  if (assetKey.startsWith('http') || assetKey.startsWith('/')) {
    return assetKey;
  }

  if (assetKey.startsWith('images_staging/') || assetKey.startsWith('images/')) {
    return assetKey;
  }

  const filename = getImageFilename(assetKey);
  if (!filename) return null;

  if (usesStagingImage(status)) {
    if (assetType === 'artist') {
      return `images_staging/${filename}`;
    }
    return `images_staging/tracks/${filename}`;
  }

  if (assetType === 'artist') {
    return `${ARTIST_IMAGES_FOLDER}/${filename}`;
  }

  return `${TRACK_IMAGES_FOLDER}/${filename}`;
};
